import { FC, useState, MouseEvent } from "react";
import cx from "classnames";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { ClickableCard } from "@/components/Card";
import Typography from "@/components/Typography";
import Button, { IconButton } from "@/components/Button";
import {
  SaveIcon,
  CheckedIcon,
  LocationIcon,
  AvatarIcon,
  BuildingIcon,
  InfoIcon,
  CalenderCheckIcon,
} from "@/components/Icon";
import Avatar from "@/components/Avatar";
import Tag from "@/components/Tag";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import { useResponsive, useDialog, useSnackbar, useTrackers } from "@/hooks";
import {
  useCreateSavedJobMutation,
  useDeleteSavedJobMutation,
} from "@/fetch/trip";
import { JobType, JOB_STATUS } from "@/fetch/suppliers";
import Tooltip from "@/components/Tooltip";
import useCategory from "./useCategory";
import useStartDates from "./useStartDates";
import styles from "./JobCard.module.scss";

type Props = {
  job?: JobType;
  isOnSaveTab?: boolean;
  onSave: () => void;
};

const JobCard: FC<Props> = ({ job, isOnSaveTab, onSave }) => {
  const [showFallbackImage, setShowFallbackImage] = useState<boolean>(false);
  const { data: personalDetails, isLoading: isPersonalDetailsLoading } =
    usePersonalDetailsQuery();
  const { mutateAsync: createSaved, isLoading: isCreatingSaved } =
    useCreateSavedJobMutation();
  const { mutateAsync: removeSaved, isLoading: isRemovingSaved } =
    useDeleteSavedJobMutation();

  const { isMobile } = useResponsive();
  const { openDialog } = useDialog("job");
  const { replace, pathname, query } = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { track } = useTrackers();
  const jobCategoryObj = useCategory(job?.categories?.[0]);
  const isSaved = job?.is_saved;
  const isInterested = job?.is_interested;
  const hasMultipleCandidates = (Number(job?.no_position) || 1) > 1;
  const isSuggested = job?.is_suggested;
  const isActive = job?.status === JOB_STATUS.Active;
  const isPlaced = job?.status === JOB_STATUS.Placed;
  const postedDate = Boolean(job?.start_date)
    ? ` ${dayjs(Number(job?.start_date) * 1000).format("MMMM")}`
    : "N/A";
  const getStartDateMonths = useStartDates();
  const startDates = getStartDateMonths(job?.dates || []);

  const skills = Boolean(job?.skill_set) ? job?.skill_set.split(",") || [] : [];

  const logoImageUrl = `https://gwatco-res.cloudinary.com/image/upload/v1684225405/${job?.s3_logo}`;

  const handleSave = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!isSaved) {
      await createSaved({ job_id: job?.id as number });
      enqueueSnackbar("Saved Successfully!", {
        variant: "success",
      });
      onSave();
    } else {
      await removeSaved({ savedJobId: job?.saved_id as number });
      enqueueSnackbar("Unsaved Successfully!", {
        variant: "success",
      });
      onSave();
    }
  };

  const handleInterest = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    replace(
      {
        pathname,
        query: { ...query, tab: "my-interests" },
      },
      undefined,
      {
        scroll: false,
      }
    );
  };

  const addFallbackImage = () => {
    setShowFallbackImage(true);
  };

  const handleOpenDialog = () => {
    if (!isActive) return;
    track("Job Card Clicked", {
      eventId: "job-card-clicked",
      jobId: job?.id,
    });
    openDialog({ id: job?.id });
  };

  return (
    <>
      <ClickableCard
        className={cx(styles.card, styles.cardMotion, {
          [styles.disabledCard]: !isActive,
        })}
        onClick={handleOpenDialog}
        outlined={!isActive}
      >
        {!isActive && (
          <div className={styles.statusContainer}>
            <InfoIcon className={styles.infoIcon} />
            <Typography color="error.main" variant="body2">
              {isPlaced
                ? "This job has been filled"
                : "Job no longer available"}
            </Typography>
          </div>
        )}
        <div className={styles.container}>
          <div
            className={cx(styles.leftContainer, {
              [styles.leftContainerSaveTab]: isOnSaveTab,
            })}
          >
            <div className={styles.topContainer}>
              <Typography
                variant="h6"
                className={styles.title}
                color={isActive ? "text.primary" : "text.secondary"}
              >
                {job?.role}
              </Typography>
              {Boolean(job?.sub_role) && (
                <Typography
                  variant="subtitle1"
                  className={styles.subtitle}
                  color="text.secondary"
                >
                  {isMobile ? job?.sub_role : <>&nbsp;&nbsp;{job?.sub_role}</>}
                </Typography>
              )}
            </div>
            <div className={styles.detailsContainer}>
              <div className={styles.detailContainer}>
                <LocationIcon className={styles.locationIcon} />
                <Typography
                  variant="body2"
                  className={styles.detailText}
                  color={isActive ? "text.primary" : "text.secondary"}
                >
                  {job?.city}
                </Typography>
              </div>
              <div className={styles.detailContainer}>
                <BuildingIcon className={styles.locationIcon} />
                <Typography
                  variant="body2"
                  className={styles.detailText}
                  color={isActive ? "text.primary" : "text.secondary"}
                >
                  {job?.partner_name}
                </Typography>
              </div>
              <Tooltip title={`Job Start Month`}>
                <div className={styles.detailContainer}>
                  <CalenderCheckIcon className={styles.locationIcon} />
                  <Typography
                    variant="body2"
                    className={styles.detailText}
                    color={isActive ? "text.primary" : "text.secondary"}
                  >
                    {startDates}
                  </Typography>
                </div>
              </Tooltip>
              {jobCategoryObj && (
                <div className={styles.detailContainer}>
                  {jobCategoryObj?.icon}
                  <Typography
                    variant="body2"
                    className={styles.detailText}
                    color={isActive ? "text.primary" : "text.secondary"}
                  >
                    {jobCategoryObj?.title}
                  </Typography>
                </div>
              )}
            </div>
            <div className={styles.detailsContainer}>
              {hasMultipleCandidates && (
                <Tooltip title="This employer has multiple positions for this job.">
                  <Typography
                    className={cx(styles.multipleCandidateContainer, {
                      [styles.disabledMultipleCandidateContainer]: !isActive,
                    })}
                    variant="body2"
                  >
                    Accepting multiple applicants
                  </Typography>
                </Tooltip>
              )}
              {Boolean(isSuggested) && !isOnSaveTab && (
                <div className={styles.suggestionContainer}>
                  <div className={styles.avatarContainer}>
                    {personalDetails?.traveller?.s3_profile_pic ? (
                      <Avatar
                        cloudinaryPath={
                          personalDetails?.traveller?.s3_profile_pic
                        }
                        className={styles.avatar}
                      />
                    ) : (
                      <AvatarIcon variant="outlined" />
                    )}
                    <div className={styles.checkIconContainer}>
                      <CheckedIcon
                        color="success"
                        className={styles.avatarCheckIcon}
                      />
                    </div>
                  </div>
                  <Typography variant="body2">
                    Your profile matches this job
                  </Typography>
                </div>
              )}
            </div>
            <Typography
              variant="body2"
              color="text.secondary"
              className={styles.summery}
            >
              {Boolean(job?.summary) ? job?.summary : job?.description}
            </Typography>
            <div className={styles.skillsContainer}>
              {skills.length > 0 &&
                skills
                  .slice(0, isMobile ? (Boolean(isInterested) ? 1 : 2) : 4)
                  .map((eachSkill) => (
                    <Tag
                      key={eachSkill}
                      label={eachSkill}
                      color="secondary"
                      className={styles.tag}
                      size="small"
                    />
                  ))}
              {((isMobile && skills.length > 2) ||
                (!isMobile && skills.length > 4) ||
                (isMobile && Boolean(isInterested) && skills.length > 1)) && (
                <Tag
                  label={`+${
                    skills.length -
                    (isMobile ? (Boolean(isInterested) ? 1 : 2) : 4)
                  }`}
                  color="secondary"
                  className={styles.tag}
                  size="small"
                />
              )}
              {Boolean(isInterested) && (
                <Button
                  variant="text"
                  className={styles.mobileInterestButton}
                  onClick={handleInterest}
                  disabled={!isActive}
                >
                  <CheckedIcon
                    variant={isOnSaveTab ? "contained" : "outlined"}
                    className={styles.checkIcon}
                  />
                  <Typography>Interested</Typography>
                </Button>
              )}
              {!Boolean(isInterested) && (
                <IconButton
                  className={cx(styles.mobileSaveButton, {
                    [styles.mobileInterestedSaveButton]: Boolean(isInterested),
                  })}
                  onClick={handleSave}
                >
                  <SaveIcon
                    variant={isSaved ? "contained" : "outlined"}
                    className={cx(styles.saveIcon, {
                      [styles.unSaveIcon]: !isSaved,
                    })}
                  />
                </IconButton>
              )}
            </div>
          </div>
          <div className={styles.rightColumn}>
            <div className={styles.actionContainer}>
              {Boolean(isInterested) && (
                <Button
                  variant="text"
                  className={styles.interestButton}
                  onClick={handleInterest}
                  disabled={!isActive}
                >
                  <CheckedIcon
                    variant={isOnSaveTab ? "contained" : "outlined"}
                    className={styles.checkIcon}
                  />
                  <Typography>Interested</Typography>
                </Button>
              )}
              {((!isInterested && !isOnSaveTab) || isOnSaveTab) && (
                <IconButton className={styles.saveButton} onClick={handleSave}>
                  <SaveIcon
                    variant={isSaved ? "contained" : "outlined"}
                    className={cx(styles.saveIcon, {
                      [styles.unSaveIcon]: !isSaved,
                    })}
                  />
                </IconButton>
              )}
            </div>
            {!showFallbackImage && (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt="logo"
                  src={logoImageUrl}
                  className={styles.logoContainer}
                  onError={addFallbackImage}
                />
              </>
            )}
          </div>
        </div>
      </ClickableCard>
    </>
  );
};

export default JobCard;
